/**
 * HubPage — accueil conversationnel pour chacun des 4 piliers Essentiels
 * (Experience, Learn, Taste, Play) et pour la rubrique Pro.
 *
 * Chaque hub :
 *  - Affiche une courte intro signée geoVINUM IA (prompte à l'action)
 *  - Propose un bouquet de "cartes d'actions" curatées pour ce pilier
 *    (les anciens sous-menus sont ici, mais présentés comme des pistes IA).
 *  - Permet une saisie libre qui se branche sur /api/ai/route — l'IA peut
 *    naviguer vers n'importe quelle page de l'app via les actions standards.
 */
import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Send, Loader2, Sparkles, MapPin, Grape, Building2, Compass, NotebookPen,
  Rocket, MessageSquare, ArrowRight, Wine, BookOpen, GraduationCap,
  CalendarDays, CalendarClock, KeyRound, Ticket, ScanLine, Camera, Zap,
  Heart, Gamepad2, Brain, LayoutGrid, ShoppingBag, Globe2, Megaphone,
  Code2, ShieldCheck,
} from "lucide-react";
import { renderMarkdown } from "@/lib/markdown";

const SESSION_PREFIX = "gv_hub_";

/* ────────── Pillar configurations ────────── */
/* Each pillar is defined by:
 *  - introKey → i18n key resolving to a 4-lang welcome bubble.
 *  - labelKey → i18n key resolving to the pillar header label.
 *  - quickActions(ctx) → array of {icon, to, labelKey, subKey} localised via useT.
 * The hub page reads these at render time with the current app language.
 */
const PILLARS = {
  experience: {
    key: "experience",
    labelKey: "hub.experience.label",
    introKey: "hub.experience.intro",
    emoji: "🎟",
    quickActions: ({ hasParticipant, isProUser }) => [
      ...(isProUser
        ? [{ icon: CalendarClock, to: "/pro/experience",
             labelKey: "sb.exp.create", subKey: "hub.exp.create.sub" }]
        : []),
      hasParticipant
        ? { icon: Ticket, to: "/experience/my",
            labelKey: "sb.exp.myTicket", subKey: "hub.exp.myTicket.sub" }
        : { icon: KeyRound, to: "/experience",
            labelKey: "sb.exp.haveCode", subKey: "hub.exp.haveCode.sub" },
      { icon: ScanLine, to: "/experience/staff",
        labelKey: "sb.exp.staff", subKey: "hub.exp.staff.sub" },
      { icon: CalendarDays, to: "/events",
        labelKey: "hub.exp.publicEvents", subKey: "hub.exp.publicEvents.sub" },
    ],
  },
  learn: {
    key: "learn",
    labelKey: "hub.learn.label",
    introKey: "hub.learn.intro",
    emoji: "📚",
    quickActions: () => [
      { icon: BookOpen, to: "/courses",
        labelKey: "sb.academyJourneys", subKey: "hub.learn.academy.sub" },
      { icon: Zap, to: "/microlearning",
        labelKey: "hub.learn.microlearning", subKey: "hub.learn.microlearning.sub" },
      { icon: GraduationCap, to: "/learn",
        labelKey: "sb.revise", subKey: "hub.learn.revise.sub" },
      { icon: MapPin, to: "/appellations",
        labelKey: "hub.learn.appellations", subKey: "hub.learn.appellations.sub" },
      { icon: Grape, to: "/cepages",
        labelKey: "sb.grapes", subKey: "hub.learn.grapes.sub" },
      { icon: Building2, to: "/producers",
        labelKey: "sb.producers", subKey: "hub.learn.producers.sub" },
      { icon: NotebookPen, to: "/carnet",
        labelKey: "sb.myNotes", subKey: "hub.learn.notes.sub" },
      { icon: Globe2, to: "/passport",
        labelKey: "sb.passport", subKey: "hub.learn.passport.sub" },
      { icon: Compass, to: "/discover",
        labelKey: "sb.mapExplorer", subKey: "hub.learn.map.sub" },
    ],
  },
  taste: {
    key: "taste",
    labelKey: "hub.taste.label",
    introKey: "hub.taste.intro",
    emoji: "👃",
    quickActions: () => [
      { icon: Sparkles, to: "/deduce",
        labelKey: "sb.deduceWine", subKey: "hub.taste.deduce.sub" },
      { icon: Heart, to: "/taste/suggestions",
        labelKey: "hub.taste.suggestions", subKey: "hub.taste.suggestions.sub" },
      { icon: Wine, to: "/taste/profile",
        labelKey: "sb.tasteProfile", subKey: "hub.taste.profile.sub" },
      { icon: Camera, to: "/explorer",
        labelKey: "hub.taste.vision", subKey: "hub.taste.vision.sub" },
    ],
  },
  play: {
    key: "play",
    labelKey: "hub.play.label",
    introKey: "hub.play.intro",
    emoji: "🎮",
    quickActions: () => [
      { icon: Brain, to: "/audrey",
        labelKey: "sb.guessAppellation", subKey: "hub.taste.guess.sub" },
      { icon: Gamepad2, to: "/play",
        labelKey: "hub.play.solo", subKey: "hub.play.solo.sub" },
      { icon: Rocket, to: "/play/multiplayer",
        labelKey: "hub.play.multi", subKey: "hub.play.multi.sub" },
      { icon: Megaphone, to: "/play/sponsored",
        labelKey: "hub.play.sponsored", subKey: "hub.play.sponsored.sub" },
      { icon: ShoppingBag, to: "/shop",
        labelKey: "hub.play.shop", subKey: "hub.play.shop.sub" },
    ],
  },
  pro: {
    key: "pro",
    labelKey: "hub.pro.label",
    introKey: "hub.pro.intro",
    emoji: "⭐",
    quickActions: () => [
      { icon: CalendarClock, to: "/pro/experience",
        labelKey: "hub.pro.exp", subKey: "hub.pro.exp.sub" },
      { icon: LayoutGrid, to: "/pro/dashboard",
        labelKey: "hub.pro.dashboard", subKey: "hub.pro.dashboard.sub" },
      { icon: Megaphone, to: "/pro/sponsored",
        labelKey: "hub.pro.sponsored", subKey: "hub.pro.sponsored.sub" },
      { icon: Code2, to: "/pro",
        labelKey: "hub.pro.showcase", subKey: "hub.pro.showcase.sub" },
    ],
  },
};

const ACTION_ICONS = {
  journey: Rocket, appellation: MapPin, cepage: Grape, producer: Building2,
  map: MapPin, nav: Compass, chat: MessageSquare,
};

export default function HubPage() {
  const { pillar: pillarKey } = useParams();
  const { lang, t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const [hasParticipant] = useState(() => !!localStorage.getItem("geovinum_participant_token"));

  const isProUser = !!(user && (user.plan === "pro" || user.role === "admin"));
  const pillar = PILLARS[pillarKey];
  const sessionId = useMemo(() => `${SESSION_PREFIX}${pillarKey || "unknown"}`, [pillarKey]);

  if (!pillar) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
        {t("hub.notFound")} <Link to="/" className="ml-1 text-[#E5276A] underline">{t("hub.backHome")}</Link>
      </div>
    );
  }

  const label = t(pillar.labelKey);
  const intro = t(pillar.introKey);
  const actions = pillar.quickActions({ hasParticipant, isProUser });

  const send = async (q) => {
    const text = (q ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/geovinum-ai/route", {
        message: text,
        session_id: sessionId,
        lang,
        context: `pillar:${pillarKey}`,
      });
      setMessages((m) => [...m, {
        role: "ai",
        text: r.data?.reply || "…",
        actions: r.data?.actions || [],
      }]);
      setTimeout(() => scrollRef.current?.scrollTo?.({ top: 9e9, behavior: "smooth" }), 60);
    } catch (e) {
      toast.error(t("hub.sendError"));
    } finally { setSending(false); }
  };

  const handleAction = (a) => {
    if (!a) return;
    if (a.href) {
      navigate(a.href);
    } else if (a.type === "chat") {
      navigate(`/chat?q=${encodeURIComponent(a?.params?.q || "")}`);
    }
  };

  return (
    <div className="min-h-[calc(100dvh_-_var(--m-header-h)_-_var(--m-tabs-h))] md:min-h-screen bg-gradient-to-b from-[#FDF9F3] via-white to-[#F5EDE2] pb-24">
      <div className="max-w-3xl mx-auto px-4 py-6" ref={scrollRef}>
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E5276A] to-[#1F3A5F] text-white flex items-center justify-center text-xl shadow-sm">
            {pillar.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-[#E5276A] font-semibold">
              {t("hub.ia.sub")}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl text-[#1F3A5F]" data-testid={`hub-title-${pillarKey}`}>
              {label}
            </h1>
          </div>
        </div>

        {/* Intro bubble */}
        <div className="mt-5 bg-white border border-[#1F3A5F]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <p className="text-sm text-neutral-700 leading-relaxed"
             dangerouslySetInnerHTML={{ __html: renderMarkdown(intro) }} />
        </div>

        {/* Quick action cards */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`hub-actions-${pillarKey}`}>
          {actions.map((a, i) => {
            const Ic = a.icon;
            return (
              <Link key={i} to={a.to}
                    data-testid={`hub-action-${pillarKey}-${i}`}
                    className="group flex items-start gap-3 bg-white hover:bg-[#FFF8FB] border border-neutral-200 hover:border-[#E5276A] rounded-2xl p-3.5 transition">
                <div className="w-10 h-10 rounded-xl bg-[#FBF7F3] text-[#E5276A] flex items-center justify-center shrink-0 group-hover:bg-[#FCE4ED]">
                  <Ic className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1F3A5F]">
                    {t(a.labelKey)}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">
                    {t(a.subKey)}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-[#E5276A] shrink-0 mt-1" />
              </Link>
            );
          })}
        </div>

        {/* Chat transcript */}
        {messages.length > 0 && (
          <div className="mt-6 space-y-3" data-testid={`hub-chat-${pillarKey}`}>
            {messages.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] bg-[#1F3A5F] text-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
                    {m.text}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex gap-3 items-start">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#E5276A] to-[#1F3A5F] text-white flex items-center justify-center text-[10px] font-bold shrink-0 shadow-sm">
                    gV·IA
                  </div>
                  <div className="flex-1 min-w-0 bg-white border border-[#1F3A5F]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                    <p className="text-sm text-neutral-700 leading-relaxed"
                       dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                    {m.actions && m.actions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.actions.map((a, k) => {
                          const Ic = ACTION_ICONS[a.type] || ArrowRight;
                          return (
                            <button key={k}
                                    onClick={() => handleAction(a)}
                                    data-testid={`hub-${pillarKey}-chat-action-${i}-${k}`}
                                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/15 hover:border-[#E5276A] hover:text-[#E5276A] transition">
                              <Ic className="w-3 h-3" />
                              {a.label || t("hub.openDefault")}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
            {sending && (
              <div className="flex gap-3 items-center text-xs text-neutral-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("hub.thinking")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat composer — sits right above the mobile bottom tabs */}
      <div className="fixed bottom-[var(--m-tabs-h)] left-0 md:left-[var(--sidebar-w)] right-0 z-30 bg-white/95 backdrop-blur border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-2 bg-white rounded-2xl border border-[#1F3A5F]/15 focus-within:border-[#E5276A] px-3 py-2 shadow-sm">
            <input value={input}
                   onChange={(e) => setInput(e.target.value)}
                   data-testid={`hub-${pillarKey}-input`}
                   placeholder={`${t("hub.placeholder")} ${label}`}
                   className="flex-1 bg-transparent outline-none text-sm text-[#1F3A5F] placeholder:text-[#8B95A1] min-w-0" />
            <Button type="submit"
                    disabled={sending || !input.trim()}
                    data-testid={`hub-${pillarKey}-send`}
                    className="h-9 bg-[#E5276A] hover:bg-[#C01661] shrink-0">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
